/**
 * 사용자 역할 관련 유틸리티 함수
 * 
 * 사용자 역할 타입과 표시 텍스트를 일관되게 관리합니다.
 */

export type UserRole = 'saas_admin' | 'admin' | 'user';

/**
 * 역할 코드를 한글 레이블로 변환
 * 
 * @param role - 역할 코드
 * @returns 한글 역할명
 * 
 * @example
 * getRoleLabel('saas_admin') // 'SaaS 관리자'
 * getRoleLabel('admin') // '기업 관리자'
 * getRoleLabel('user') // '일반 사용자'
 */
export const getRoleLabel = (role: string): string => {
  const roleMap: Record<string, string> = {
    saas_admin: 'SaaS 관리자',
    admin: '기업 관리자',
    user: '일반 사용자',
  };
  return roleMap[role] || role;
};

/**
 * 모든 역할 옵션 반환 (Select 컴포넌트용)
 * 
 * @returns 역할 옵션 배열
 */
export const getRoleOptions = () => [
  { value: 'all', label: '전체' },
  { value: 'saas_admin', label: 'SaaS 관리자' },
  { value: 'admin', label: '기업 관리자' },
  { value: 'user', label: '일반 사용자' },
];

/**
 * 역할 권한 레벨 반환 (높을수록 높은 권한)
 * 
 * @param role - 역할 코드
 * @returns 권한 레벨 (0-2)
 */
export const getRoleLevel = (role: string): number => {
  const levelMap: Record<string, number> = {
    user: 0,
    admin: 1,
    saas_admin: 2,
  };
  return levelMap[role] ?? -1;
};

/**
 * 역할 A가 역할 B보다 높은 권한인지 확인
 * 
 * @param roleA - 비교할 역할 A
 * @param roleB - 비교할 역할 B
 * @returns A가 B보다 높은 권한이면 true
 */
export const hasHigherRole = (roleA: string, roleB: string): boolean => {
  return getRoleLevel(roleA) > getRoleLevel(roleB);
};

/**
 * 역할이 관리자급인지 확인
 * 
 * @param role - 역할 코드
 * @returns 관리자급이면 true
 */
export const isAdminRole = (role: string): boolean => {
  return role === 'admin' || role === 'saas_admin';
};

/**
 * 역할이 SaaS 관리자인지 확인
 * 
 * @param role - 역할 코드
 * @returns SaaS 관리자면 true
 */
export const isSaasAdminRole = (role: string): boolean => {
  return role === 'saas_admin';
};

